"use client";

import { useState } from "react";
import styles from "./EmbedFeaturesTable.module.css";

const PLANS = [
  { id: "starter", name: "Starter" },
  { id: "pro", name: "Pro" },
  { id: "ultra", name: "Ultra" },
];

const ROWS = [
  { name: "Hosted videos", values: { free: "1", starter: "3", pro: "15", ultra: "50" } },
  { name: "Extra videos", values: { free: false, starter: "$0.25/m each", pro: "$0.25/m each", ultra: "$0.25/m each" } },
  { name: "Bandwidth/mo", values: { free: "50 GB", starter: "250 GB", pro: "1 TB", ultra: "3 TB" } },
  { name: "Bandwidth overage", values: { free: false, starter: "$0.25/GB", pro: "$0.25/GB", ultra: "$0.20/GB" } },
  { name: "Team members", values: { free: "1", starter: "1", pro: "3", ultra: "10+" } },
  { name: "Workspaces", values: { free: "1", starter: "1", pro: "5", ultra: "unlimited" } },
  { name: "Player themes", values: { free: "Clay only", starter: "All themes", pro: "All themes", ultra: "All themes" } },
  { name: "Color customization", values: { free: false, starter: true, pro: true, ultra: true } },
  { name: "Flare branding hidden", values: { free: false, starter: true, pro: true, ultra: true } },
  { name: "Plays count", values: { free: true, starter: true, pro: true, ultra: true } },
  { name: "Analytics", values: { free: false, starter: true, pro: true, ultra: true } },
  { name: "Heatmaps & single-user breakdown", values: { free: false, starter: false, pro: true, ultra: true } },
  { name: "A/B testing", values: { free: false, starter: false, pro: true, ultra: true } },
  { name: "Engagement optimization", values: { free: false, starter: false, pro: true, ultra: true } },
  { name: "Password protection", values: { free: false, starter: true, pro: true, ultra: true } },
  { name: "Chapters", values: { free: false, starter: true, pro: true, ultra: true } },
  { name: "Multi-language subtitles", values: { free: false, starter: true, pro: true, ultra: true } },
  { name: "Editor exports", values: { free: false, starter: true, pro: true, ultra: true } },
  { name: "Domain whitelisting", values: { free: false, starter: false, pro: false, ultra: true } },
  { name: "Live chat support", values: { free: true, starter: true, pro: true, ultra: true } },
];

const CheckIcon = () => (
  <svg className={styles.checkIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-label="Yes">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const DashIcon = () => (
  <span className={styles.dashIcon} aria-label="Not included">—</span>
);

function renderValue(value) {
  if (value === true) return <CheckIcon />;
  if (value === false) return <DashIcon />;
  return <span className={styles.textValue}>{value}</span>;
}

export default function EmbedFeaturesTable() {
  const [selectedMobilePlan, setSelectedMobilePlan] = useState("pro");

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <h3 className={styles.title}>Compare all features</h3>

        {/* Desktop table */}
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.featureHeader}>Features</th>
                {PLANS.map((p) => (
                  <th key={p.id} className={`${styles.planHeader} ${p.id === "pro" ? styles.planHeaderHighlight : ""}`}>
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.name} className={styles.featureRow}>
                  <td className={styles.featureName}>{row.name}</td>
                  {PLANS.map((p) => (
                    <td
                      key={p.id}
                      className={`${styles.featureValue} ${p.id === "pro" ? styles.featureValueHighlight : ""}`}
                    >
                      {renderValue(row.values[p.id])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile table — single plan column with selector */}
        <div className={styles.mobileWrapper}>
          <div className={styles.mobilePlanSelector} role="tablist">
            {PLANS.map((p) => (
              <button
                key={p.id}
                role="tab"
                aria-selected={selectedMobilePlan === p.id}
                className={`${styles.mobilePlanBtn} ${selectedMobilePlan === p.id ? styles.mobilePlanBtnActive : ""}`}
                onClick={() => setSelectedMobilePlan(p.id)}
              >
                {p.name}
              </button>
            ))}
          </div>
          <table className={styles.mobileTable}>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.name} className={styles.featureRow}>
                  <td className={styles.featureName}>{row.name}</td>
                  <td
                    className={`${styles.featureValue} ${
                      selectedMobilePlan === "pro" ? styles.featureValueHighlight : ""
                    }`}
                  >
                    {renderValue(row.values[selectedMobilePlan])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
